// PowerSync React provider.
//
// Wraps the app in a PowerSyncContext.Provider so all child hooks
// (useQuery, etc.) can access the db singleton. On mount, connects
// the singleton to the backend connector AND opens an explicit
// subscription on every sync stream the app needs. On unmount,
// unsubscribes and disconnects so the sync stream is torn down
// cleanly (important in test environments where providers mount
// and unmount frequently).
//
// Why explicit subscribe(): in powersync-js 1.52, the sync-streams
// API is alpha and the server-side `auto_subscribe: true` flag in
// powersync/sync-config.yaml is *not* sufficient on its own — the
// SDK still requires the client to subscribe before any bucket is
// delivered. Without these subscribe calls, `db.connect()` returns,
// the WS opens, the JWT is accepted, and then every /sync/stream
// request closes in a few ms because there's nothing to send. The
// status stays `connecting: true / connected: false` forever, no
// `ps_buckets` rows are ever written, and screens that gate on
// `useAllSyncStreamsHaveSynced(...)` (e.g. BaseDetailPage's site
// list) spin indefinitely. See docs/adr/002-powersync-offline-sync.md
// and the SyncStream type in @powersync/common's sync-streams.d.ts.
//
// Error handling: connector errors (network failures, backend 5xx)
// are surfaced via an error banner rather than letting them propagate
// silently. The 401 → redirect path is handled by the connector
// itself (navigateTo('/auth/login')), so we only need to handle
// non-auth errors here. Subscribe failures are logged but do not
// surface to the UI — a single failed stream should not block the
// rest of the app from rendering.
//
// Provider ordering in main.tsx: PowerSyncProvider must be INSIDE
// AuthProvider so it only runs after the auth check has resolved.
// The auth check (GET /me) confirms the session cookie is valid
// before we start minting PowerSync tokens.
//
// See docs/plans/research/powersync-frontend-api.md §4 for the
// PowerSyncContext.Provider pattern used here.

// Imports are split across single-line statements rather than bundled
// into a multi-line block so that lint:architecture (Rule 4 — direct
// PowerSync imports allowed inside hooks/) recognises each as a real
// import. Continuation lines of a multi-line import look like
// non-import string matches to the awk-based scanner. Mirrors the
// same pattern used by `connector.ts` in this directory.
import { useEffect, useState, type ReactNode } from 'react'
import { PowerSyncContext } from '@powersync/react'
import type { SyncStreamSubscription } from '@powersync/web'

import { isOfflineNetworkError } from '../../app/auth-cache'
import { useAuth } from '../../app/auth-provider'

import { db } from './client'
import { connector } from './connector'

/**
 * Streams the app subscribes to on mount. Mirrors the four streams
 * declared in `powersync/sync-config.yaml`. Server-side they are all
 * `auto_subscribe: true`; this client-side list is what actually
 * causes the SDK to request bucket delivery.
 */
const SYNC_STREAM_NAMES = [
  'reference_global',
  'reference_site_scoped',
  'assignments',
  'captures',
] as const

export interface PowerSyncProviderProps {
  children: ReactNode
  /** Test seam — inject a mock db instance. */
  database?: typeof db
  /** Test seam — inject a mock connector. */
  dbConnector?: typeof connector
}

export function PowerSyncProvider({
  children,
  database = db,
  dbConnector = connector,
}: PowerSyncProviderProps): ReactNode {
  const [connectError, setConnectError] = useState<Error | null>(null)

  useEffect(() => {
    let active = true
    const subscriptions: SyncStreamSubscription[] = []

    database
      .connect(dbConnector)
      .then(() => {
        if (!active) return
        // Fire each subscribe independently and track each subscription
        // the moment its own promise resolves — NOT after a Promise.allSettled
        // batch. Reason: if one stream's subscribe() hangs (partial
        // handshake on a flaky mobile network, server-side stuck stream),
        // an allSettled await would block forever and the other already-
        // resolved subscriptions would never make it into `subscriptions`
        // — they'd then leak past unmount because the cleanup path below
        // only iterates what we tracked. Per-promise tracking guarantees
        // every successful subscribe is either pushed to `subscriptions`
        // (then cleaned up at unmount) or unsubscribed immediately if it
        // resolves after unmount. (Codex P2 on this PR.)
        for (const name of SYNC_STREAM_NAMES) {
          database
            .syncStream(name)
            .subscribe()
            .then((sub) => {
              if (!active) {
                sub.unsubscribe()
                return
              }
              subscriptions.push(sub)
            })
            .catch((err: unknown) => {
              console.warn(
                'PowerSync stream subscribe failed:',
                err instanceof Error ? err.message : err,
              )
            })
        }
      })
      .catch((err: unknown) => {
        if (!active) return
        // Suppress the "Offline sync unavailable" banner when this is
        // genuinely an offline failure (TypeError from fetch, etc. —
        // see auth-cache.isOfflineNetworkError). The capture flow is
        // designed to keep working from local SQLite while the device
        // is offline, so a red banner saying sync is broken would be
        // actively misleading. Real config / 5xx errors still surface
        // because we want loud signal on those.
        if (isOfflineNetworkError(err)) return
        const error = err instanceof Error ? err : new Error(String(err))
        setConnectError(error)
      })

    return () => {
      active = false
      // Unsubscribe before disconnect so the SDK's CRUD/upload paths
      // don't observe a torn-down DB on the way out.
      for (const sub of subscriptions) {
        sub.unsubscribe()
      }
      // Disconnect on unmount so tests that mount/unmount providers
      // don't leak open WebSocket connections or workers.
      void database.disconnect()
    }
  }, [database, dbConnector])

  return (
    <PowerSyncContext.Provider value={database}>
      {connectError ? (
        // Simple banner — Phase D-1 scaffolding. D-2+ can promote to
        // a proper toast or error boundary if needed.
        <div
          role="alert"
          aria-live="assertive"
          className="bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-2"
        >
          Offline sync unavailable: {connectError.message}
        </div>
      ) : null}
      {children}
    </PowerSyncContext.Provider>
  )
}

/**
 * Auth-gated wrapper around `PowerSyncProvider`. Renders children
 * directly while the auth bootstrap is pending or not yet
 * authenticated; only mounts the underlying `PowerSyncProvider` (and
 * therefore only triggers `fetchCredentials` → `/powersync/token`)
 * once `useAuth().status === 'authenticated'`.
 *
 * Why this is necessary (Codex P2 on PR #391):
 *   On a deep-link visit like `/capture/sites/<id>` while the cookie
 *   is missing or expired, AuthProvider starts in 'loading' and
 *   resolves to 'unauthenticated' shortly after `/me` returns 401.
 *   If `PowerSyncProvider` mounts during that window, its connector
 *   races ahead, calls `/powersync/token`, gets a 401, and falls into
 *   the connector's `navigateTo('/auth/login')` path — which does NOT
 *   carry the `redirect_to` query param that `RequireAuth` would have
 *   added. The user signs in and lands on `/capture` instead of the
 *   original deep target. Gating on 'authenticated' here means the
 *   only redirect path is the one that goes through `RequireAuth`,
 *   which preserves the URL.
 *
 * Why a wrapper (not an inline `useAuth()` inside `PowerSyncProvider`):
 *   The base provider is unit-tested by mounting it in isolation
 *   without an `AuthProvider`. Adding a `useAuth()` call inside the
 *   base provider would force every existing test to wrap with
 *   `AuthProvider`, weakening their isolation. Splitting the gate
 *   into a tiny, separately-tested wrapper keeps the mounts cheap and
 *   the test seams orthogonal.
 *
 * Children are rendered without a `PowerSyncContext.Provider` while
 * the gate is closed. That is fine because:
 *   - The login route does not use `useQuery` / `useBases` / etc.
 *   - `RequireAuth` is the gate for `/capture/*` routes; it shows a
 *     splash while auth is `'loading'` and redirects (with
 *     `redirect_to`) on `'unauthenticated'`. So no `useQuery` hook
 *     ever mounts before the underlying `PowerSyncProvider` has been
 *     wrapped around the tree.
 */
export function AuthGatedPowerSyncProvider({
  children,
  database = db,
  dbConnector = connector,
}: PowerSyncProviderProps): ReactNode {
  const { status } = useAuth()

  if (status !== 'authenticated') {
    return <>{children}</>
  }

  return (
    <PowerSyncProvider database={database} dbConnector={dbConnector}>
      {children}
    </PowerSyncProvider>
  )
}
